import {
  ParticipantTabFormKey,
  ProgramClosureFormKey,
} from "../../pages/createProgram/modelsAndSchemas/Models";
import { SITE_CORE_COUNTRIES_VALUE } from "../constants";

export type FieldRuleConfig = {
  visible: boolean;
  mandatory: boolean;
  showDropdownForOptionalCountry?: boolean; // for showing state dropdown for the optional country US and Canada for stateProvince field
};

export const mandatoryCountriesForGender = [
  SITE_CORE_COUNTRIES_VALUE.COUNTRY_BELGIUM,
  SITE_CORE_COUNTRIES_VALUE.FRANCE,
  SITE_CORE_COUNTRIES_VALUE.SPAIN,
];

export const mandatoryCountriesForDateOfBirth = mandatoryCountriesForGender;
export const mandatoryCountriesForOccupationalFunction =
  mandatoryCountriesForGender;
export const mandatoryCountriesForCurrentEmploymentStartDate =
  mandatoryCountriesForGender;
export const mandatoryCountriesForTotalYearsEmployed =
  mandatoryCountriesForGender;

export const mandatoryCountriesForAgeRange = [
  SITE_CORE_COUNTRIES_VALUE.BELGIUM,
  SITE_CORE_COUNTRIES_VALUE.FRANCE,
  SITE_CORE_COUNTRIES_VALUE.SPAIN,
  SITE_CORE_COUNTRIES_VALUE.ITALY,
];

export const optionalCountriesForSocialFund = [
  SITE_CORE_COUNTRIES_VALUE.BELGIUM,
];

export const optionalCountriesForParticipantGroupType = [
  SITE_CORE_COUNTRIES_VALUE.BELGIUM,
];

export const mandatoryCountriesForEducationLevel = [
  SITE_CORE_COUNTRIES_VALUE.BRAZIL,
  SITE_CORE_COUNTRIES_VALUE.FRANCE,
  SITE_CORE_COUNTRIES_VALUE.SPAIN,
  SITE_CORE_COUNTRIES_VALUE.BELGIUM,
];

export const mandatoryCountriesForPersonalId = [
  SITE_CORE_COUNTRIES_VALUE.SPAIN,
];

export const mandatoryCountriesForStateRegionProvince = [
  SITE_CORE_COUNTRIES_VALUE.BELGIUM,
  SITE_CORE_COUNTRIES_VALUE.FRANCE,
  SITE_CORE_COUNTRIES_VALUE.SPAIN,
  SITE_CORE_COUNTRIES_VALUE.BRAZIL,
];

export const nonMandatoryCountriesForStateRegionProvince = [
  SITE_CORE_COUNTRIES_VALUE.UNITED_STATES,
  SITE_CORE_COUNTRIES_VALUE.CANADA,
];

export const mandatoryCountriesForPostalCode = [
  SITE_CORE_COUNTRIES_VALUE.COUNTRY_BELGIUM,
  SITE_CORE_COUNTRIES_VALUE.FRANCE,
  SITE_CORE_COUNTRIES_VALUE.SPAIN,
];

const fieldRules: Record<
  string,
  {
    mandatoryCountries?: string[];
    optionalCountries?: string[];
    hiddenCountries?: string[];
    alwaysVisible?: boolean;
    defaultVisible?: boolean;
    specialHandler?: (countryCode: string) => FieldRuleConfig;
  }
> = {
  [ParticipantTabFormKey.gender]: {
    mandatoryCountries: mandatoryCountriesForGender,
    defaultVisible: false,
  },
  [ParticipantTabFormKey.dateOfBirth]: {
    mandatoryCountries: mandatoryCountriesForDateOfBirth,
    defaultVisible: false,
  },
  [ParticipantTabFormKey.stateProvince]: {
    specialHandler: (countryCode) => {
      if (mandatoryCountriesForStateRegionProvince.includes(countryCode)) {
        return { visible: true, mandatory: true };
      }
      if (nonMandatoryCountriesForStateRegionProvince.includes(countryCode)) {
        return {
          visible: true,
          mandatory: false,
          showDropdownForOptionalCountry: true,
        };
      }
      return { visible: true, mandatory: false };
    },
  },
  [ParticipantTabFormKey.postalCode]: {
    mandatoryCountries: mandatoryCountriesForPostalCode,
    alwaysVisible: true,
  },
  [ParticipantTabFormKey.ageRange]: {
    mandatoryCountries: mandatoryCountriesForAgeRange,
    alwaysVisible: true,
  },
  [ParticipantTabFormKey.educationLevel]: {
    mandatoryCountries: mandatoryCountriesForEducationLevel,
    alwaysVisible: true,
  },
  [ParticipantTabFormKey.socialFund]: {
    optionalCountries: optionalCountriesForSocialFund,
    defaultVisible: false,
  },
  [ParticipantTabFormKey.participantGroupType]: {
    optionalCountries: optionalCountriesForParticipantGroupType,
    defaultVisible: false,
  },
  [ParticipantTabFormKey.personalId]: {
    mandatoryCountries: mandatoryCountriesForPersonalId,
    defaultVisible: false,
  },
  [ParticipantTabFormKey.occupationalFunction]: {
    mandatoryCountries: mandatoryCountriesForOccupationalFunction,
    alwaysVisible: true,
  },
  [ParticipantTabFormKey.currentEmploymentStartDate]: {
    mandatoryCountries: mandatoryCountriesForCurrentEmploymentStartDate,
    alwaysVisible: true,
  },
  [ParticipantTabFormKey.totalYearsEmployed]: {
    mandatoryCountries: mandatoryCountriesForTotalYearsEmployed,
    alwaysVisible: true,
  },
};

export const getFieldRuleByCountry = (
  countryCode: string,
  fieldKey: string,
): FieldRuleConfig => {
  const rule = fieldRules[fieldKey];

  if (!rule) {
    return { visible: true, mandatory: false };
  }

  if (rule.specialHandler) {
    return rule.specialHandler(countryCode);
  }

  const isMandatory = rule.mandatoryCountries?.includes(countryCode) ?? false;
  const isOptional = rule.optionalCountries?.includes(countryCode) ?? false;

  if (isMandatory) {
    return { visible: true, mandatory: true };
  }

  if (isOptional) {
    return { visible: true, mandatory: false };
  }

  const visible = rule.alwaysVisible ?? rule.defaultVisible ?? false;
  return { visible, mandatory: false };
};

export const mandatoryCountriesForNewEmployeeName = [
  SITE_CORE_COUNTRIES_VALUE.BELGIUM,
  SITE_CORE_COUNTRIES_VALUE.SPAIN,
  SITE_CORE_COUNTRIES_VALUE.MEXICO,
];

export const mandatoryCountriesForNewTitle = [
  SITE_CORE_COUNTRIES_VALUE.BELGIUM,
  SITE_CORE_COUNTRIES_VALUE.FRANCE,
  SITE_CORE_COUNTRIES_VALUE.SPAIN,
  SITE_CORE_COUNTRIES_VALUE.JAPAN,
];

export const mandatoryCountriesForProgramClosurePostalCode = [
  SITE_CORE_COUNTRIES_VALUE.BELGIUM,
  SITE_CORE_COUNTRIES_VALUE.FRANCE,
  SITE_CORE_COUNTRIES_VALUE.JAPAN,
  SITE_CORE_COUNTRIES_VALUE.AUSTRALIA,
  SITE_CORE_COUNTRIES_VALUE.MEXICO,
  SITE_CORE_COUNTRIES_VALUE.BRAZIL,
];

// this will be for functional change, new occupational function and other functions, etc.
export const mandatoryCountriesForProgramClosureFunctionalChange = [
  SITE_CORE_COUNTRIES_VALUE.BELGIUM,
  SITE_CORE_COUNTRIES_VALUE.FRANCE,
  SITE_CORE_COUNTRIES_VALUE.SPAIN,
  SITE_CORE_COUNTRIES_VALUE.JAPAN,
];

export const mandatoryCountriesForProgramClosureIndustryChange = [
  SITE_CORE_COUNTRIES_VALUE.BELGIUM,
  SITE_CORE_COUNTRIES_VALUE.FRANCE,
  SITE_CORE_COUNTRIES_VALUE.SPAIN,
];

export const mandatoryCountriesForProgramClosureEndDate = [
  SITE_CORE_COUNTRIES_VALUE.BELGIUM,
  SITE_CORE_COUNTRIES_VALUE.FRANCE,
];

export const mandatoryCountriesForProgramGeographicalMove = [
  SITE_CORE_COUNTRIES_VALUE.FRANCE,
  SITE_CORE_COUNTRIES_VALUE.ITALY,
  SITE_CORE_COUNTRIES_VALUE.BELGIUM,
];

export const mandatoryCountriesForProgramCurrentSalaryRange = [
  SITE_CORE_COUNTRIES_VALUE.BELGIUM,
  SITE_CORE_COUNTRIES_VALUE.FRANCE,
  SITE_CORE_COUNTRIES_VALUE.SPAIN,
];

export const mandatoryCountriesForProgramNewSalaryRange = [
  SITE_CORE_COUNTRIES_VALUE.FRANCE,
  SITE_CORE_COUNTRIES_VALUE.SPAIN,
];

export const mandatoryCountriesForProgramNewEmployerVATNumber = [
  SITE_CORE_COUNTRIES_VALUE.SPAIN,
];

export const mandatoryCountriesForProgramLandingCountry = [
  SITE_CORE_COUNTRIES_VALUE.BELGIUM,
  SITE_CORE_COUNTRIES_VALUE.FRANCE,
  SITE_CORE_COUNTRIES_VALUE.SPAIN,
];

export const mandatoryCountriesForProgramNewLevel = [
  SITE_CORE_COUNTRIES_VALUE.BELGIUM,
  SITE_CORE_COUNTRIES_VALUE.FRANCE,
  SITE_CORE_COUNTRIES_VALUE.ITALY,
  SITE_CORE_COUNTRIES_VALUE.SPAIN,
];

export const mandatoryCountriesForProgramClosureState = [
  SITE_CORE_COUNTRIES_VALUE.BELGIUM,
  SITE_CORE_COUNTRIES_VALUE.FRANCE,
  SITE_CORE_COUNTRIES_VALUE.SPAIN,
  SITE_CORE_COUNTRIES_VALUE.BRAZIL,
];

export const hiddenCountriesForProgramClosureState = [
  SITE_CORE_COUNTRIES_VALUE.UNITED_STATES,
  SITE_CORE_COUNTRIES_VALUE.CANADA,
];

export const mandatoryCountriesForSolutionValidationDate = [
  SITE_CORE_COUNTRIES_VALUE.BELGIUM,
  SITE_CORE_COUNTRIES_VALUE.FRANCE,
  SITE_CORE_COUNTRIES_VALUE.JAPAN,
  SITE_CORE_COUNTRIES_VALUE.AUSTRALIA,
  SITE_CORE_COUNTRIES_VALUE.INDIA,
];

export const mandatoryCountriesForNewEmploymentContractType = [
  SITE_CORE_COUNTRIES_VALUE.BELGIUM,
  SITE_CORE_COUNTRIES_VALUE.FRANCE,
  SITE_CORE_COUNTRIES_VALUE.SPAIN,
  SITE_CORE_COUNTRIES_VALUE.ITALY,
  SITE_CORE_COUNTRIES_VALUE.JAPAN,
  SITE_CORE_COUNTRIES_VALUE.MEXICO,
  SITE_CORE_COUNTRIES_VALUE.BRAZIL,
];

const landingFieldRules: Record<
  string,
  {
    mandatoryCountries?: string[];
    hiddenCountries?: string[];
    defaultVisible?: boolean;
    defaultMandatory?: boolean;
    specialHandler?: (countryCode: string) => FieldRuleConfig;
  }
> = {
  [ProgramClosureFormKey.newEmployeeName]: {
    mandatoryCountries: mandatoryCountriesForNewEmployeeName,
  },
  [ProgramClosureFormKey.newTitle]: {
    mandatoryCountries: mandatoryCountriesForNewTitle,
  },
  [ProgramClosureFormKey.postalCode]: {
    mandatoryCountries: mandatoryCountriesForProgramClosurePostalCode,
  },
  [ProgramClosureFormKey.functionalChange]: {
    mandatoryCountries: mandatoryCountriesForProgramClosureFunctionalChange,
  },
  [ProgramClosureFormKey.industryChange]: {
    mandatoryCountries: mandatoryCountriesForProgramClosureIndustryChange,
  },
  [ProgramClosureFormKey.dateOfEndOfTrialPeriod]: {
    mandatoryCountries: mandatoryCountriesForProgramClosureEndDate,
  },
  [ProgramClosureFormKey.geographicalMove]: {
    mandatoryCountries: mandatoryCountriesForProgramGeographicalMove,
  },
  [ProgramClosureFormKey.currentSalaryRange]: {
    mandatoryCountries: mandatoryCountriesForProgramCurrentSalaryRange,
  },
  [ProgramClosureFormKey.newSalaryRange]: {
    mandatoryCountries: mandatoryCountriesForProgramNewSalaryRange,
  },
  [ProgramClosureFormKey.newEmployerVATNumber]: {
    mandatoryCountries: mandatoryCountriesForProgramNewEmployerVATNumber,
  },
  [ProgramClosureFormKey.landingCountry]: {
    mandatoryCountries: mandatoryCountriesForProgramLandingCountry,
  },
  [ProgramClosureFormKey.newLevel]: {
    mandatoryCountries: mandatoryCountriesForProgramNewLevel,
  },
  [ProgramClosureFormKey.state]: {
    specialHandler: (countryCode) => {
      if (mandatoryCountriesForProgramClosureState.includes(countryCode)) {
        return { visible: true, mandatory: true };
      }
      if (hiddenCountriesForProgramClosureState.includes(countryCode)) {
        return { visible: false, mandatory: false };
      }
      return { visible: true, mandatory: false };
    },
  },
  [ProgramClosureFormKey.solutionValidationDate]: {
    mandatoryCountries: mandatoryCountriesForSolutionValidationDate,
    defaultVisible: true,
  },
  [ProgramClosureFormKey.newEmploymentContractType]: {
    mandatoryCountries: mandatoryCountriesForNewEmploymentContractType,
    defaultVisible: true,
  },
};

export const getFieldRuleByCountryForLandingInformation = (
  countryCode: string,
  fieldKey: string,
): FieldRuleConfig => {
  const rule = landingFieldRules[fieldKey];

  if (!rule) {
    return { visible: true, mandatory: false };
  }

  if (rule.specialHandler) {
    return rule.specialHandler(countryCode);
  }

  const isMandatory = rule.mandatoryCountries?.includes(countryCode) ?? false;

  if (isMandatory) {
    return { visible: true, mandatory: true };
  }

  const visible = rule.defaultVisible ?? false;
  const mandatory = rule.defaultMandatory ?? false;

  return { visible, mandatory };
};
